/**
 * HTTP Client
 *
 * Axios-based HTTP client for making real API requests.
 * Handles authentication, error handling, and response transformation.
 */

import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import type { ApiResponse, PaginatedResponse } from '../types'
import { apiConfig } from './config'
import { setAuthCookie, readAuthCookie, clearAuthCookie } from '../lib/authCookie'

// Session storage key for tokens
const TOKEN_KEY = 'docurious-tokens'

interface StoredTokens {
  accessToken: string
  refreshToken: string
  expiresAt: string
}

/**
 * Get stored tokens from localStorage
 */
export function getStoredTokens(): StoredTokens | null {
  const stored = localStorage.getItem(TOKEN_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored)
  } catch {
    return null
  }
}

/**
 * Store tokens in localStorage
 */
export function storeTokens(tokens: StoredTokens): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
}

/**
 * Clear stored tokens
 */
export function clearStoredTokens(): void {
  localStorage.removeItem(TOKEN_KEY)
}

/**
 * Check if token is expired
 */
export function isTokenExpired(expiresAt: string): boolean {
  return new Date(expiresAt) < new Date()
}

/**
 * Create the axios instance
 */
function createHttpClient(): AxiosInstance {
  const client = axios.create({
    baseURL: apiConfig.baseUrl,
    timeout: apiConfig.timeout,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })

  // Request interceptor - add auth token
  client.interceptors.request.use(
    (config) => {
      const tokens = getStoredTokens()
      if (tokens && !isTokenExpired(tokens.expiresAt)) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // Response interceptor - handle errors
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

      // Handle 401 Unauthorized
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true

        const tokens = getStoredTokens()
        if (tokens?.refreshToken) {
          try {
            // Try to refresh the token
            const response = await axios.post(`${apiConfig.baseUrl}/auth/refresh`, {
              refreshToken: tokens.refreshToken,
            })

            const newTokens = response.data.data.tokens
            storeTokens(newTokens)

            // Update shared auth cookie (preserve displayName from existing cookie)
            const existing = readAuthCookie()
            setAuthCookie(newTokens, existing?.displayName ?? '')

            // Retry the original request
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`
            }
            return client(originalRequest)
          } catch {
            // Refresh failed - clear tokens and redirect to login
            clearStoredTokens()
            clearAuthCookie()
            window.location.href = '/login'
          }
        } else {
          clearStoredTokens()
          clearAuthCookie()
          window.location.href = '/login'
        }
      }

      return Promise.reject(error)
    }
  )

  return client
}

// Export the singleton instance
export const httpClient = createHttpClient()

// Install debug interceptors in development mode
if (import.meta.env.DEV) {
  import('./debugInterceptor').then(({ installDebugInterceptors }) => {
    installDebugInterceptors(httpClient)
  })
}

/**
 * Transform API response to our standard format
 */
function transformResponse<T>(response: AxiosResponse): ApiResponse<T> {
  // Handle different backend response formats
  const data = response.data

  // If backend returns our expected format
  if (typeof data === 'object' && 'success' in data) {
    return data as ApiResponse<T>
  }

  // Otherwise, wrap it
  return {
    success: true,
    data: data as T,
  }
}

/**
 * Transform error to our standard format
 */
function transformError<T>(error: AxiosError): ApiResponse<T> {
  const message =
    (error.response?.data as { message?: string; error?: string })?.message ||
    (error.response?.data as { message?: string; error?: string })?.error ||
    error.message ||
    'An unexpected error occurred'

  return {
    success: false,
    data: null as unknown as T,
    error: message,
  }
}

/**
 * HTTP request helpers with consistent response format
 */
export const http = {
  /**
   * GET request
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await httpClient.get<T>(url, config)
      return transformResponse<T>(response)
    } catch (error) {
      return transformError<T>(error as AxiosError)
    }
  },

  /**
   * POST request
   */
  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await httpClient.post<T>(url, data, config)
      return transformResponse<T>(response)
    } catch (error) {
      return transformError<T>(error as AxiosError)
    }
  },

  /**
   * PUT request
   */
  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await httpClient.put<T>(url, data, config)
      return transformResponse<T>(response)
    } catch (error) {
      return transformError<T>(error as AxiosError)
    }
  },

  /**
   * PATCH request
   */
  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await httpClient.patch<T>(url, data, config)
      return transformResponse<T>(response)
    } catch (error) {
      return transformError<T>(error as AxiosError)
    }
  },

  /**
   * DELETE request
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await httpClient.delete<T>(url, config)
      return transformResponse<T>(response)
    } catch (error) {
      return transformError<T>(error as AxiosError)
    }
  },

  /**
   * GET request with pagination
   */
  async getPaginated<T>(url: string, config?: AxiosRequestConfig): Promise<PaginatedResponse<T>> {
    try {
      const response = await httpClient.get<PaginatedResponse<T>>(url, config)
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      return {
        success: false,
        data: [],
        error:
          (axiosError.response?.data as { message?: string })?.message ||
          axiosError.message ||
          'An unexpected error occurred',
        pagination: {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0,
          hasMore: false,
        },
      }
    }
  },

  /**
   * Upload file with multipart/form-data
   */
  async upload<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await httpClient.post<T>(url, formData, {
        ...config,
        headers: {
          ...config?.headers,
          'Content-Type': 'multipart/form-data',
        },
      })
      return transformResponse<T>(response)
    } catch (error) {
      return transformError<T>(error as AxiosError)
    }
  },
}
